"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRecentSearch = void 0;
const react_1 = require("react");
const profanity_1 = require("../services/profanity");
/**
 * Custom hook to manage recent search items in local storage.
 * Provides methods to add, remove, and clear recent searches.
 *
 * @param {string} lsKey - The key to store/retrieve the recent search data in local storage.
 * @param {number} max - The maximum number of recent searches to keep.
 * @returns {Object} An object containing the recent search items and methods to modify them:
 *   - {Array} items - The current list of recent searches.
 *   - {Function} add - Method to add a new search to the list.
 *   - {Function} remove - Method to remove a search from the list.
 *   - {Function} clearAll - Method to clear all recent searches.
 */
function useRecentSearch(lsKey, max, dataPersistence = true, checkProfanity = true) {
    const [items, setItems] = (0, react_1.useState)([]);
    const isDataPersistence = (0, react_1.useRef)(dataPersistence);
    const add = (val, id, callback) => {
        if (checkProfanity && (0, profanity_1.hasProfanity)(val)) {
            return;
        }
        const uniqueId = id ?? Date.now() + Math.floor(Math.random() * 1000);
        const filteredSearches = items.filter((item) => item.label.toLowerCase() !== val.toLowerCase());
        const newEntry = [
            { id: uniqueId.toString() || val, label: val },
            ...filteredSearches,
        ].slice(0, max);
        if (isDataPersistence.current)
            localStorage.setItem(lsKey, JSON.stringify(newEntry));
        setItems(newEntry);
        callback?.(newEntry);
    };
    const clearAll = (callback) => {
        if (isDataPersistence.current)
            localStorage.removeItem(lsKey);
        setItems([]);
        callback?.([]);
    };
    const remove = (id, callback) => {
        const newEntry = items.filter((item) => item.id !== id);
        if (isDataPersistence.current)
            localStorage.setItem(lsKey, JSON.stringify(newEntry));
        setItems(newEntry);
        callback?.(newEntry);
    };
    const fetch = () => {
        try {
            let prevEntries = localStorage.getItem(lsKey);
            if (prevEntries) {
                prevEntries = JSON.parse(prevEntries);
                if (Array.isArray(prevEntries))
                    setItems(prevEntries.slice(0, max));
            }
        }
        catch (err) {
            console.error("Looks like cookies or localstorage blocked!", err);
        }
    };
    const toggleDataPersistence = (persist) => {
        if (persist === undefined) {
            isDataPersistence.current = !isDataPersistence.current;
        }
        else {
            isDataPersistence.current = persist;
        }
    };
    const replace = (items, dataPersistence = true) => {
        if (dataPersistence)
            localStorage.setItem(lsKey, JSON.stringify(items));
        setItems(items);
    };
    (0, react_1.useEffect)(() => {
        if (isDataPersistence.current)
            fetch();
    }, [isDataPersistence.current]);
    return {
        items,
        add,
        replace,
        remove,
        clearAll,
        refresh: fetch,
        toggleDataPersistence,
    };
}
exports.useRecentSearch = useRecentSearch;
